"use client";

import { useCrmHoteleiroPage } from "./hooks/use-crm-hoteleiro-page";
import { useSeo } from "@/features/shared/lib/use-seo";
import {
  HeroSection,
  RecursosSection,
} from "./components";
import { GenericInfoSection } from "@/features/shared/components/generic-info-section";
import { INFO_SECTIONS } from "@/features/shared/content/info-sections";

// Provas Sociais importadas da Home e UI
import { NumerosSection } from "@/features/home/components/numeros-section";
import { NaMidiaSection } from "@/features/home/components/na-midia-section";
import { SoftwareProductsCarousel, TrustedLogosMarquee, WallOfLoveSection } from "@/features/shared/components";
import { FAQAccordion } from "@/features/ui/components/faq-accordion";
import { LeadCaptureCTA } from "@/features/ui/components/lead-capture-cta";
import { useLeadCapture } from "@/features/shared/lib/lead-capture-context";
import { UltimasDoBlogSection } from "@/features/shared/components/ultimas-do-blog-section-lazy";

import { Spinner } from "@/components/ui/spinner";
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyContent } from "@/components/ui/empty";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

import { artigosMidia, depoimentos, numeros, videosData } from "@/features/shared/data/social-proof-data";

const LEAD_TITLE = "CRM Hoteleiro";

function CrmHoteleiroPage() {
  useSeo({
    title: "CRM Hoteleiro: Fidelize e Aumente Reservas | Foco",
    description:
      "CRM desenvolvido para hotelaria: segmente hóspedes, automatize campanhas e aumente a recorrência de reservas diretas no seu hotel.",
    path: "/crm-hoteleiro",
  });

  const { openLeadCapture } = useLeadCapture();
  const { data, isLoading, isError, refetch } = useCrmHoteleiroPage();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="w-12 h-12" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Empty className="text-center">
          <EmptyHeader>
            <EmptyTitle>Erro ao carregar dados</EmptyTitle>
            <EmptyDescription>
              Não foi possível carregar o conteúdo da página. Tente novamente.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button onClick={() => refetch()} variant="outline" className="rounded-full px-6">
              <RefreshCw className="w-4 h-4 mr-2" />
              Tentar novamente
            </Button>
          </EmptyContent>
        </Empty>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Section 1: Hero */}
      <HeroSection
        data={data.hero}
        onCtaClick={() => openLeadCapture({ source: "form-crm-hoteleiro", title: LEAD_TITLE })}
      />

      {/* Section 2: O que é um CRM */}
      <GenericInfoSection {...INFO_SECTIONS.crmHoteleiro} imageSide="right" />

      {/* Section 3: Recursos (com título/subtítulo customizado) */}
      <RecursosSection />

      {/* Prova Social: Mural de depoimentos (texto + vídeo, unificados) */}
      <WallOfLoveSection
        depoimentos={depoimentos}
        videos={videosData}
        badge="Depoimentos"
        title="Um mural de resultados reais"
        subtitle="Cada card aqui é um hoteleiro de verdade, contando como a Foco aumentou a retenção de hóspedes."
      />

      {/* Prova Social: Na Mídia */}
      <NaMidiaSection artigos={artigosMidia} />

      {/* Bloco de Autoridade e Confiança */}
      <NumerosSection numeros={numeros} />
      <TrustedLogosMarquee />

      {/* Prova Social: Carrossel de Produtos */}
      <SoftwareProductsCarousel />

      <UltimasDoBlogSection productSlug="crm-hoteleiro" />

      {/* Prova Social: FAQ */}
      <FAQAccordion
        items={[
          { id: "1", question: "O que é um CRM Hoteleiro?", answer: "Um CRM Hoteleiro é um sistema de gestão de relacionamento com o cliente que centraliza dados de hóspedes, permite segmentação, automação de marketing e criação de campanhas personalizadas para fidelização." },
          { id: "2", question: "Como o CRM ajuda na fidelização?", answer: "O CRM permite conhecer profundamente cada hóspede, suas preferências e histórico. Com esses dados, você pode criar ofertas personalizadas, enviar lembretes de datas especiais e oferecer experiências únicas que incentivam o retorno." },
          { id: "3", question: "Quais integrações o CRM oferece?", answer: "Nosso CRM integra com PMS, Motor de Reservas, Channel Manager e ferramentas de marketing como RD Station, Mailchimp, Meta Ads e Google Ads, proporcionando uma visão 360° do cliente." },
          { id: "4", question: "Como funciona a automação de marketing?", answer: "Você cria fluxos automáticos de e-mail e WhatsApp que são disparados baseados em comportamentos do cliente: aniversário, aniversário de casamento, pós-estadia, recuperação de orçamentos e muito mais." },
          { id: "5", question: "As pesquisas de satisfação são personalizáveis?", answer: "Sim! Você pode criar pesquisas personalizadas com perguntas específicas sobre a experiência do hóspede e receber os resultados em tempo real para tomar decisões rápidas." },
        ]}
        title="Dúvidas Frequentes"
        subtitle="Tire suas dúvidas sobre o CRM Hoteleiro"
        badge="FAQ"
        showContactButton
      />

      {/* Lead Capture CTA */}
      <LeadCaptureCTA
        title="Pronto para transformar dados em hóspedes fiéis?"
        subtitle="Solicite uma demonstração e descubra como o CRM da Foco pode aumentar sua fidelização e reservas diretas."
        badge="Comece agora"
        source="crm-hoteleiro"
        leadTitle={LEAD_TITLE}
      />
    </div>
  );
}

export { CrmHoteleiroPage };
